import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from "@mui/material";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { useCreateUser, useRoles, useUpdateUser, useUser, type UserPayload } from "./api";
import { errorMessageFor } from "../../api/client";
import { useNotify } from "../../app/useNotify";
import { roleLabel } from "../../lib/labels";

const schema = z.object({
  email: z.string().min(1, "This field is required").email("Invalid email format."),
  firstName: z.string().min(1, "This field is required"),
  lastName: z.string(),
  password: z.string(),
  roleIds: z.array(z.number()).min(1, "Pick at least one role"),
});

type FormValues = z.infer<typeof schema>;

interface Props {
  open: boolean;
  userId: number | null;
  onClose: () => void;
}

export function UserFormDialog({ open, userId, onClose }: Props) {
  const editing = userId !== null;
  const roles = useRoles();
  const existing = useUser(open && editing ? userId : null);
  const create = useCreateUser();
  const update = useUpdateUser();
  const notify = useNotify();

  const { control, register, handleSubmit, reset, formState } = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: "onChange",
    defaultValues: { email: "", firstName: "", lastName: "", password: "", roleIds: [] },
  });

  useEffect(() => {
    if (!open) return;
    if (!editing) {
      reset({ email: "", firstName: "", lastName: "", password: "", roleIds: [] });
      return;
    }
    if (existing.data) {
      reset({
        email: existing.data.email,
        firstName: existing.data.firstName ?? "",
        lastName: existing.data.lastName ?? "",
        password: "",
        // The detail endpoint repeats each role once per authority it
        // carries, so the same id arrives several times. Posting that back
        // multiplies the user's role rows.
        roleIds: [...new Set(existing.data.roles.map((role) => role.id))],
      });
    }
  }, [open, editing, existing.data, reset]);

  const onSubmit = handleSubmit(async (values) => {
    const payload: UserPayload = {
      email: values.email,
      firstName: values.firstName,
      lastName: values.lastName,
      roles: [...new Set(values.roleIds)].map((id) => ({ id })),
    };
    // On edit an empty password field means "leave it alone".
    if (values.password) payload.password = values.password;

    try {
      if (editing) {
        await update.mutateAsync({ userId, payload });
        notify.success("User updated");
      } else {
        await create.mutateAsync({ ...payload, password: values.password });
        notify.success("User created");
      }
      onClose();
    } catch (error) {
      notify.error(errorMessageFor(error) ?? "Could not save the user.");
    }
  });

  const busy = create.isPending || update.isPending;
  // Until the existing user has loaded, the reset below would overwrite
  // anything already typed, so the fields are not rendered yet.
  const loading = editing && !existing.data;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{editing ? "Edit user" : "New user"}</DialogTitle>
      <Box component="form" onSubmit={onSubmit} noValidate>
        <DialogContent>
          {loading ? (
            <Box sx={{ display: "grid", placeItems: "center", py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              <TextField
                {...register("email")}
                label="Email"
                required
                fullWidth
                margin="normal"
                disabled={editing}
                error={Boolean(formState.errors.email)}
                helperText={formState.errors.email?.message}
              />
              <TextField
                {...register("firstName")}
                label="First name"
                required
                fullWidth
                margin="normal"
                error={Boolean(formState.errors.firstName)}
                helperText={formState.errors.firstName?.message}
              />
              <TextField {...register("lastName")} label="Last name" fullWidth margin="normal" />
              <TextField
                {...register("password")}
                label={editing ? "New password" : "Password"}
                required={!editing}
                type="password"
                fullWidth
                margin="normal"
                error={Boolean(formState.errors.password)}
                helperText={
                  formState.errors.password?.message ??
                  (editing ? "Leave blank to keep the current password." : undefined)
                }
              />
              <Controller
                control={control}
                name="roleIds"
                render={({ field }) => (
                  <TextField
                    select
                    label="Roles"
                    required
                    fullWidth
                    margin="normal"
                    value={field.value}
                    onChange={(event) =>
                      field.onChange(
                        (typeof event.target.value === "string"
                          ? event.target.value.split(",")
                          : (event.target.value as unknown as number[])
                        ).map(Number),
                      )
                    }
                    slotProps={{ select: { multiple: true } }}
                    error={Boolean(formState.errors.roleIds)}
                    helperText={formState.errors.roleIds?.message}
                  >
                    {(roles.data ?? []).map((role) => (
                      <MenuItem key={role.id} value={role.id}>
                        {roleLabel(role.name)}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={busy || loading || !formState.isValid}
          >
            {editing ? "Save changes" : "Create user"}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
